import keyBy from 'lodash/keyBy'

import { excludeAbortError } from '@app/errors/AbortError'

import { createSelector } from '@app/utils/createSelector'
import { normalizeSafe } from '@app/utils/normalizer'

import type { DepersonalizedAnnouncementsSearchMeta } from '@app/packages/api/v2/actions/depersonalized_announcements'
import type { ApiDepersonalizedAnnouncement } from '@app/packages/api/v2/types/entities'

import { createReduxSlice } from '@app/store/redux_slice'
import { createThunk } from '@app/store/thunk'

import type { PointsMapPoint } from '@app/components/PointsMap/PointsMapPoint'

const modelsSlice = createReduxSlice<{ [key: string]: ApiDepersonalizedAnnouncement }>('depersonalized_announcements_models')
const listSlice = createReduxSlice<string[]>('depersonalized_announcements_list')
const mapSlice = createReduxSlice<{ [key: string]: PointsMapPoint }>('depersonalized_announcements_map')
const metaSlice = createReduxSlice<Meta>('depersonalized_announcements_meta')
const countSlice = createReduxSlice<Count>('depersonalized_announcements_count')

/** Announcements read by id only fill the models — no list, no paging. */
export const setDepersonalizedAnnouncementsSlice = (payload: unknown) =>
  modelsSlice.update(state => ({ ...state, ...normalizeSafe<ApiDepersonalizedAnnouncement>(payload).depersonalized_announcements }))

export const addDepersonalizedAnnouncementsPageSlice = (resp: { data: ApiDepersonalizedAnnouncement[]; meta: DepersonalizedAnnouncementsSearchMeta }) =>
  createThunk(dispatch => {
    dispatch(setDepersonalizedAnnouncementsSlice(resp))
    const ids = resp.data.map(x => x.id)
    dispatch(listSlice.update(state => (resp.meta.current_page < 2 ? ids : (state ?? EMPTY_LIST).concat(ids))))
    dispatch(metaSlice.update(state => ({ ...state, cursor: resp.meta.cursor, fetched: resp.data.length === 0, loading: false })))
    dispatch(countSlice.update(state => ({ ...(state ?? DEFAULT_COUNT), value: resp.meta.total, fetched: true })))
  })

export const startDepersonalizedAnnouncementsSlice = () => metaSlice.update(state => ({ ...state, loading: true }))

export const failDepersonalizedAnnouncementsSlice = () => metaSlice.update(state => ({ ...state, loading: false }))

export const setDepersonalizedAnnouncementsMapSlice = (points: PointsMapPoint[], { initial }: { initial: boolean }) =>
  mapSlice.update(state => (initial ? keyBy(points, 'id') : { ...state, ...keyBy(points, 'id') }))

export const startDepersonalizedAnnouncementsCountSlice = () => countSlice.update(state => ({ ...(state ?? DEFAULT_COUNT), loading: true, error: null }))

export const setDepersonalizedAnnouncementsCountSlice = (value: number) => countSlice.set({ fetched: true, loading: false, value, error: null })

export const failDepersonalizedAnnouncementsCountSlice = (error: Error) =>
  countSlice.update(state => ({ ...(state ?? DEFAULT_COUNT), loading: false, error: excludeAbortError(error) }))

export const depersonalizedAnnouncementsModelsSelector = createSelector([modelsSlice.selector], models => models ?? {})
export const depersonalizedAnnouncementsIdListSelector = createSelector([listSlice.selector], list => list ?? EMPTY_LIST)
export const depersonalizedAnnouncementsMapPointsSelector = createSelector([mapSlice.selector], points => points ?? {})
export const depersonalizedAnnouncementsMetaSelector = createSelector([metaSlice.selector], meta => meta ?? DEFAULT_META)
export const depersonalizedAnnouncementsCountSelector = createSelector([countSlice.selector], count => count?.value ?? 0)

type Meta = { cursor?: string; fetched?: boolean; loading?: boolean }

type Count = { fetched: boolean; loading: boolean; value: number; error: Error | null }

const EMPTY_LIST: string[] = []

const DEFAULT_META: Meta = {}

const DEFAULT_COUNT: Count = { fetched: false, loading: false, value: 0, error: null }
